import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { CalendlyInline } from "@/components/CalendlyBooking";
import { CoveButton } from "@/components/CoveButton";
import { ArchImage } from "@/components/ArchImage";
import { SITE, CALENDLY_SESSIONS, IMAGES } from "@/data/content";

function SessionDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = CALENDLY_SESSIONS.find((s) => s.id === value) || CALENDLY_SESSIONS[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="session-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 rounded-full bg-cove-cream border border-cove-slate/20 hover:border-cove-slate/50 px-6 py-4 text-left transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cove-slate/40"
      >
        <span>
          <span className="block text-[10px] uppercase tracking-[0.28em] text-cove-taupe">
            Session length
          </span>
          <span className="mt-0.5 block font-serif text-lg text-cove-slate">
            {selected.label}
          </span>
        </span>
        <ChevronDown
          size={18}
          className={`text-cove-slate transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute z-40 mt-2 w-full rounded-2xl bg-cove-cream border border-cove-slate/15 shadow-[0_20px_60px_-25px_rgba(37,44,54,0.4)] p-2"
            data-testid="session-dropdown-menu"
          >
            {CALENDLY_SESSIONS.map((s) => {
              const isActive = s.id === value;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-testid={`session-option-${s.id}`}
                    onClick={() => {
                      onChange(s.id);
                      setOpen(false);
                    }}
                    className={`w-full text-left rounded-xl px-4 py-3 transition-colors ${
                      isActive
                        ? "bg-cove-slate/[0.08]"
                        : "hover:bg-cove-slate/[0.05]"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-serif text-lg text-cove-slate">{s.label}</span>
                      <span className="text-[10px] uppercase tracking-[0.24em] text-cove-taupe">
                        {s.duration}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-cove-olive leading-snug">{s.description}</p>
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Book() {
  const [params, setParams] = useSearchParams();
  const initial =
    CALENDLY_SESSIONS.find((s) => s.id === params.get("session"))?.id ||
    CALENDLY_SESSIONS[0].id;
  const [selectedId, setSelectedId] = useState(initial);
  const selected = CALENDLY_SESSIONS.find((s) => s.id === selectedId) || CALENDLY_SESSIONS[0];

  useEffect(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("session", selectedId);
        return next;
      },
      { replace: true }
    );
  }, [selectedId, setParams]);

  return (
    <>
      <Seo
        title="Book a Session"
        description="Book your craniosacral therapy session with Laura McLelland at Cranio Cove in Irvington, NY. Choose a 30-minute or 1-hour session using the online scheduler."
        path="/book"
      />
      <PageHero
        overline="Booking"
        titleLines={["Reserve your", "time to rest"]}
        intro="Choose your session length below. You'll receive a confirmation, and Laura will send a short intake form ahead of your first visit."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "Book", path: "/book" }])}
      />

      <ArchImage
        src={IMAGES.coveDockPink}
        alt="A wooden dock reaching into a still lake at pink dusk"
        maxWidth={520}
        height={460}
        testId="book-arch"
      />

      <section className="px-6 md:px-12 pb-6 md:pb-8" data-testid="book-session-selector">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <SessionDropdown value={selectedId} onChange={setSelectedId} />
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-10 md:pb-14" data-testid="book-calendly">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p
              className="mb-3 text-xs text-cove-taupe uppercase tracking-[0.28em]"
              data-testid="book-calendly-active-label"
            >
              Scheduling: {selected.label}
            </p>
            <CalendlyInline key={selected.id} url={selected.url} />
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-16 md:pb-20" data-testid="book-intake">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div className="rounded-[2rem] bg-cove-sage/20 border border-cove-slate/10 px-8 md:px-12 py-10 text-center">
              <h2 className="font-serif text-3xl md:text-4xl text-cove-slate">New to Cranio Cove?</h2>
              <p className="mt-3 max-w-xl mx-auto text-cove-olive leading-relaxed">
                Please complete the confidential client intake form before your first session so
                Laura can prepare a session tailored to you.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                <CoveButton to="/intake" variant="primary" testId="book-intake-button">Complete Intake Form</CoveButton>
                <CoveButton to="/contact" variant="secondary" testId="book-contact-button">Ask a Question</CoveButton>
              </div>
              <p className="mt-5 text-sm text-cove-taupe">Prefer to talk first? Call {SITE.phone}.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
